import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useAdminMFA } from '@/hooks/useAdminMFA';

interface DisableMFADialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  onSuccess?: () => void;
}

export function DisableMFADialog({
  open,
  onOpenChange,
  userId,
  userEmail,
  onSuccess,
}: DisableMFADialogProps) {
  const [reason, setReason] = useState('');
  const { disableUserMFA, isDisabling } = useAdminMFA();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      return;
    }

    try {
      await disableUserMFA(userId, reason.trim());
      setReason('');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Error handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Disable User 2FA
          </DialogTitle>
          <DialogDescription>
            Disable two-factor authentication for: <strong>{userEmail}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will immediately disable 2FA and delete all backup codes for this user.
              They will be able to log in without 2FA. This action is logged and the user will be notified.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason for disabling MFA <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="User reported lost access to authenticator device and backup codes..."
              rows={4}
              required
              disabled={isDisabling}
            />
            <p className="text-sm text-muted-foreground">
              This reason will be logged in the audit trail and sent to the user.
            </p>
          </div>

          <Alert variant="destructive">
            <AlertDescription>
              <strong>Note:</strong> You may also need to manually unenroll TOTP factors
              via the Supabase Dashboard under Authentication → Users if the user still cannot log in.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isDisabling}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isDisabling || !reason.trim()}
            >
              {isDisabling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
